import { useEffect, useRef, useState } from "react";

/**
 * Debounces the loading state to prevent flickering on rapid loading changes.
 * Shows loader immediately when loading starts, but delays hiding to avoid flash.
 * @param isLoading - Current loading state from data fetching
 * @param delay - Milliseconds to wait before hiding loader (default: 300ms)
 * @returns Debounced loading state for UI display
 */
export const useLoading = (isLoading: boolean, delay = 300) => {
  const [showLoader, setShowLoader] = useState(isLoading);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isLoading) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setShowLoader(true);
      return;
    }

    timeoutRef.current = setTimeout(() => {
      setShowLoader(false);
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isLoading, delay]);

  return showLoader;
};
